import React, { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { DashboardLayout } from '@xanectech/components/layout';
import { Loading } from '@xanectech/components/shared';
import {
  PortalAccessDeniedPage,
  RequireAuthWrapper,
  useAuth,
  usePortalLoginOutcome,
} from '@xanectech/auth';
import { useCompanyDashboardConfig } from './lib/useCompanyDashboardConfig';
import { createLogger } from '@xanectech/lib';
const logger = createLogger('Company - AppRoutes');
import { CompanySubscriptionProvider } from '@xanectech/data';

const LoadingPage = (): React.ReactElement => <Loading spinner='Xanec' />;

const resolveLazyComponent = (
  moduleValue: unknown,
  exportName: string,
): React.ComponentType<Record<string, unknown>> => {
  if (typeof moduleValue !== 'object' || moduleValue === null) {
    logger.warn('Lazy module was not an object', { exportName });
    return LoadingPage;
  }

  const moduleRecord = moduleValue as Record<string, unknown>;
  const exportedValue = moduleRecord[exportName];

  if (typeof exportedValue === 'function') {
    return exportedValue as React.ComponentType<Record<string, unknown>>;
  }

  logger.warn('Lazy module export missing or invalid', { exportName });
  return LoadingPage;
};

const lazyNamedExport = (
  loader: () => Promise<unknown>,
  exportName: string,
): React.LazyExoticComponent<React.ComponentType<Record<string, unknown>>> =>
  lazy(() =>
    loader().then((moduleValue) => ({
      default: resolveLazyComponent(moduleValue, exportName),
    })),
  );

// Lazy-loaded pages for code splitting
const CompanyDashboardPage = lazyNamedExport(
  () => import('@/pages/CompanyDashboardPage'),
  'CompanyDashboardPage',
);
const NotFound = lazyNamedExport(
  () => import('@xanectech/components/shared'),
  'NotFoundPage',
);
const AuthPage = lazyNamedExport(() => import('@xanectech/auth'), 'AuthPage');
const LogoutPage = lazyNamedExport(
  () => import('@/pages/LogoutPage'),
  'LogoutPage',
);

const CompanyMessagesPageLazy = lazyNamedExport(
  () => import('@/pages/CompanyMessagesPage'),
  'CompanyMessagesPage',
);

const CompanyDivisionsPage = lazyNamedExport(
  () => import('@/pages/CompanyDivisionsPage'),
  'CompanyDivisionsPage',
);

const CompanyUsersPage = lazyNamedExport(
  () => import('@/pages/CompanyUsersPage'),
  'CompanyUsersPage',
);

const ContractAnalyticsPage = lazyNamedExport(
  () => import('@/pages/ContractAnalyticsPage'),
  'ContractAnalyticsPage',
);

const ContractSharingPage = lazyNamedExport(
  () => import('@/pages/ContractSharingPage'),
  'ContractSharingPage',
);

const CategoryManagementPage = lazyNamedExport(
  () => import('@/pages/CategoryManagementPage'),
  'CategoryManagementPage',
);

const CompanySettingsPage = lazyNamedExport(
  () => import('@/pages/CompanySettingsPage'),
  'CompanySettingsPage',
);

const DataContractsPage = lazyNamedExport(
  () => import('@/pages/DataContractsPage'),
  'DataContractsPage',
);

const EditContractPage = lazyNamedExport(
  () => import('@/pages/EditContractPage'),
  'EditContractPage',
);

const ViewContractPage = lazyNamedExport(
  () => import('@/pages/ViewContractPage'),
  'ViewContractPage',
);

const CreateContractPage = lazyNamedExport(
  () => import('@/pages/CreateContractPage'),
  'CreateContractPage',
);

const CompanyAnalyticsPage = lazyNamedExport(
  () => import('@/pages/CompanyAnalyticsPage'),
  'CompanyAnalyticsPage',
);

const ContractSubmissionsPage = lazyNamedExport(
  () => import('@/pages/ContractSubmissionsPage'),
  'ContractSubmissionsPage',
);

const FieldManagementPage = lazyNamedExport(
  () => import('@/pages/FieldManagementPage'),
  'FieldManagementPage',
);

const publicRoutes: string[] = ['/auth', '/logout'];

// Loading fallback component
const RouteLoadingFallback = () => <Loading spinner='Xanec' />;

function CompanyRoutes(): React.ReactElement {
  const dashboard = useCompanyDashboardConfig();
  const { portal, user } = useAuth();
  logger.info('CompanyRoutes render', { dashboard });

  usePortalLoginOutcome({
    currentRole:
      dashboard.status === 'access-denied' ? dashboard.currentRole : null,
    denialReason:
      dashboard.status === 'access-denied' ? dashboard.reason : undefined,
    email: user?.email,
    outcome:
      dashboard.status === 'authenticated'
        ? 'success'
        : dashboard.status === 'access-denied'
          ? 'denied'
          : 'pending',
    portal,
    requestedPath: `${window.location.pathname}${window.location.search}`,
    userId: user?.id,
  });

  if (dashboard.status === 'loading') {
    return <Loading spinner='Xanec' />;
  }

  if (dashboard.status === 'access-denied') {
    return <PortalAccessDeniedPage currentRole={dashboard.currentRole} />;
  }

  if (dashboard.status === 'unauthenticated') {
    return (
      <RequireAuthWrapper publicRoutes={publicRoutes}>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route index element={<CompanyDashboardPage />} />
            <Route
              path='/auth'
              element={<AuthPage allowSignup={false} redirectPath='/' />}
            />
            <Route path='/logout' element={<LogoutPage />} />
            <Route path='*' element={<NotFound showDashboard showHome />} />
          </Routes>
        </Suspense>
      </RequireAuthWrapper>
    );
  }

  return (
    <RequireAuthWrapper publicRoutes={publicRoutes}>
      <CompanySubscriptionProvider>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route
              path='/'
              element={<DashboardLayout config={dashboard.config!} />}
            >
              <Route index element={<CompanyDashboardPage />} />
              <Route path='/messages' element={<CompanyMessagesPageLazy />} />
              <Route path='/divisions' element={<CompanyDivisionsPage />} />
              <Route path='/users' element={<CompanyUsersPage />} />
              <Route path='/settings' element={<CompanySettingsPage />} />
              <Route path='/data-contracts' element={<DataContractsPage />} />
              <Route
                path='data-contracts/create'
                element={<CreateContractPage />}
              />
              <Route
                path='data-contracts/:id/edit'
                element={<EditContractPage />}
              />
              <Route
                path='data-contracts/:id/view'
                element={<ViewContractPage />}
              />
              <Route
                path='data-contracts/:id/analytics'
                element={<ContractAnalyticsPage />}
              />
              <Route
                path='data-contracts/:id/sharing'
                element={<ContractSharingPage />}
              />
              <Route
                path='data-contracts/:id/submissions'
                element={<ContractSubmissionsPage />}
              />
              <Route
                path='/data-categories'
                element={<CategoryManagementPage />}
              />
              <Route path='/data-fields' element={<FieldManagementPage />} />
              <Route path='/analytics' element={<CompanyAnalyticsPage />} />
            </Route>
            <Route
              path='/auth'
              element={<AuthPage allowSignup={false} redirectPath='/' />}
            />
            <Route path='/logout' element={<LogoutPage />} />

            <Route path='*' element={<NotFound />} />
          </Routes>
        </Suspense>
      </CompanySubscriptionProvider>
    </RequireAuthWrapper>
  );
}
export { CompanyRoutes };
